<template>
  <iframe
    :key="frameKey"
    ref="frame"
    class="full-html-frame"
    :srcdoc="content"
    title="Hotsite"
    scrolling="auto"
    sandbox="allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
    allowfullscreen
    @load="setupFrame"
  ></iframe>
</template>

<script>
export default {
  name: "FullHtmlFrame",

  props: {
    content: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      frameKey: 0,
    };
  },

  watch: {
    content: {
      immediate: true,
      handler(value) {
        this.frameKey += 1;
        this.setMetadata(value);
      },
    },
  },

  mounted() {
    this.setScrollLock(true);
  },

  destroyed() {
    this.setScrollLock(false);
  },

  methods: {
    setScrollLock(locked) {
      document.documentElement.classList.toggle("full-html-mode", locked);
      document.body.classList.toggle("full-html-mode", locked);
    },

    setMetadata(html) {
      if (!html || typeof DOMParser === "undefined") return;

      try {
        const htmlDocument = new DOMParser().parseFromString(html, "text/html");
        const title = htmlDocument.querySelector("title");
        const favicon = htmlDocument.querySelector("link[rel~='icon']");
        const currentFavicon = document.getElementById("favicon");

        if (title && title.textContent) document.title = title.textContent;
        if (favicon && favicon.getAttribute("href") && currentFavicon) {
          currentFavicon.href = favicon.getAttribute("href");
        }
      } catch (e) {
        console.log(e);
      }
    },

    setupFrame() {
      const frame = this.$refs.frame;
      const frameDocument = frame && frame.contentDocument;
      const frameWindow = frame && frame.contentWindow;
      if (!frameDocument || frameDocument.__btScrollGuardInstalled) return;

      frameDocument.__btScrollGuardInstalled = true;

      const isInternalTarget = (value) =>
        typeof value === "string" && value.trim().charAt(0) === "#";

      const findTarget = (selector) => {
        const target = selector.trim();
        if (!target || target === "#") return frameDocument.documentElement;
        const id = target.replace("#", "");

        try {
          return (
            frameDocument.getElementById(id) ||
            frameDocument.querySelector(`[name="${id}"]`) ||
            frameDocument.querySelector(target)
          );
        } catch (e) {
          return frameDocument.getElementById(id);
        }
      };

      const scrollToTarget = (selector) => {
        const target = findTarget(selector);
        if (!target) return false;

        if (frameWindow && target.getBoundingClientRect) {
          const top =
            target === frameDocument.documentElement
              ? 0
              : target.getBoundingClientRect().top + frameWindow.pageYOffset;

          frameWindow.scrollTo({ top, behavior: "smooth" });
        } else {
          target.scrollIntoView({ behavior: "smooth" });
        }

        return true;
      };

      const isScrollControl = (element) => {
        if (!element) return false;

        const inlineAction = (
          element.getAttribute("onclick") || ""
        ).toLowerCase();
        const target =
          element.getAttribute("href") ||
          element.getAttribute("data-target") ||
          element.getAttribute("data-scroll-target") ||
          element.getAttribute("aria-controls");

        return (
          isInternalTarget(target) ||
          inlineAction.includes("scrollto") ||
          inlineAction.includes("scrollintoview")
        );
      };

      frameDocument.querySelectorAll("button:not([type])").forEach((button) => {
        if (isScrollControl(button)) button.setAttribute("type", "button");
      });

      frameDocument.addEventListener(
        "click",
        (event) => {
          const control = event.target.closest("a, button, [role='button']");
          if (!control) return;

          const href = control.getAttribute("href");
          if (isInternalTarget(href)) {
            if (scrollToTarget(href)) event.preventDefault();
            return;
          }

          if (
            control.tagName === "BUTTON" &&
            control.form &&
            !control.hasAttribute("type") &&
            isScrollControl(control)
          ) {
            control.setAttribute("type", "button");
          }
        },
        true,
      );

      frameDocument.addEventListener(
        "submit",
        (event) => {
          const form = event.target;
          const submitter = event.submitter || frameDocument.activeElement;
          const formAction = form && form.getAttribute("action");
          const isAccidentalInternalSubmit =
            submitter &&
            submitter.tagName === "BUTTON" &&
            !submitter.hasAttribute("type") &&
            (!formAction || isInternalTarget(formAction));

          if (isAccidentalInternalSubmit) event.preventDefault();
        },
        true,
      );
    },
  },
};
</script>

<style scoped>
.full-html-frame {
  position: fixed;
  inset: 0;
  display: block;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  border: 0;
  margin: 0;
  padding: 0;
  background: #fff;
  overflow: auto;
}
</style>
